import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatEUR, formatDateFR, formatApiError, photoUrl } from "../../lib/api";
import AuthImg from "./AuthImg";
import { toast } from "sonner";
import { confirmAction } from "../ConfirmDialog";
import { Loader2, Plus, Trash2, Wrench, AlertTriangle, CheckCircle2, X, Camera, ShieldAlert, Paperclip, FileText } from "lucide-react";
import SupplierPicker from "./SupplierPicker";
import { MaintenancePlanSection } from "./MaintenancePlan";

export const MAINT_TYPES = [
  { value: "entretien", label: "Entretien courant" },
  { value: "vidange", label: "Vidange" },
  { value: "freins", label: "Freins" },
  { value: "pneus", label: "Pneus" },
  { value: "controle_technique", label: "Contrôle technique" },
  { value: "reparation", label: "Réparation mécanique" },
  { value: "carrosserie", label: "Carrosserie" },
  { value: "nettoyage", label: "Nettoyage" },
  { value: "depannage", label: "Dépannage" },
  { value: "autre", label: "Autre" },
];

export const MAINT_STATUSES = {
  to_plan: { label: "À planifier", cls: "bg-slate-100 text-slate-600" },
  planned: { label: "Planifiée", cls: "bg-blue-100 text-blue-700" },
  in_progress: { label: "En cours", cls: "bg-amber-100 text-amber-700" },
  done: { label: "Terminée", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Annulée", cls: "bg-red-100 text-red-700" },
};

const SEVERITIES = {
  low: { label: "Faible", cls: "bg-slate-100 text-slate-600" },
  medium: { label: "Moyenne", cls: "bg-amber-100 text-amber-700" },
  high: { label: "Haute", cls: "bg-red-100 text-red-700" },
};

const ISSUE_STATUSES = {
  open: { label: "Ouvert", cls: "bg-red-50 text-red-700 border-red-200" },
  in_progress: { label: "En cours", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  resolved: { label: "Résolu", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 outline-none";
const labelCls = "text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500";

// ─── Onglet Entretien ────────────────────────────────────────────────────────

const EMPTY_MAINT = { type: "entretien", title: "", date: "", status: "planned", cost: "", estimated_cost: "", provider: "", supplier_id: null, mileage: "", notes: "", next_due_date: "", next_due_km: "", block_vehicle: false, block_end_date: "" };

export function MaintenanceTab({ vehicleId }) {
  const [records, setRecords] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_MAINT);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/vendor/vehicles/${vehicleId}/maintenance`).then((r) => setRecords(r.data.records || []));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [vehicleId]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/vendor/vehicles/${vehicleId}/maintenance`, {
        type: form.type,
        title: form.title.trim(),
        date: form.date,
        status: form.status,
        cost: form.cost === "" ? null : parseFloat(form.cost),
        estimated_cost: form.estimated_cost === "" ? null : parseFloat(form.estimated_cost),
        provider: form.provider.trim(),
        supplier_id: form.supplier_id || null,
        mileage: form.mileage === "" ? null : parseInt(form.mileage, 10),
        notes: form.notes.trim(),
        next_due_date: form.next_due_date || null,
        next_due_km: form.next_due_km === "" ? null : parseInt(form.next_due_km, 10),
        block_vehicle: form.block_vehicle,
        block_end_date: form.block_vehicle ? (form.block_end_date || form.date) : null,
      });
      toast.success("Intervention enregistrée");
      setForm(EMPTY_MAINT);
      setShowForm(false);
      load();
      window.dispatchEvent(new Event("taskcenter-changed"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!(await confirmAction({
      title: "Supprimer cette intervention ?",
      message: "L'intervention d'entretien sera définitivement supprimée de l'historique du véhicule.",
      confirmLabel: "Supprimer l'intervention",
      variant: "danger",
    }))) return;
    try {
      await api.delete(`/vendor/vehicles/${vehicleId}/maintenance/${id}`);
      toast.success("Intervention supprimée");
      load();
      window.dispatchEvent(new Event("taskcenter-changed"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const setStatus = async (rec, status) => {
    try {
      await api.patch(`/vendor/vehicles/${vehicleId}/maintenance/${rec.id}`, { status });
      toast.success(status === "done" ? "Intervention terminée" : status === "in_progress" ? "Intervention rouverte" : "Statut mis à jour");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const uploadReceipt = async (rec, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vendor/vehicles/${vehicleId}/maintenance/${rec.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Justificatif ajouté");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const deleteReceipt = async (rec, path) => {
    try {
      await api.delete(`/vendor/vehicles/${vehicleId}/maintenance/${rec.id}/receipt?path=${encodeURIComponent(path)}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const openReceipt = async (path) => {
    try {
      const res = await api.get(`/files/${path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Impossible d'ouvrir le justificatif");
    }
  };

  if (records === null) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>;

  const totalCost = records.reduce((s, r) => s + (r.cost || 0), 0);

  return (
    <div className="space-y-4" data-testid="maintenance-tab">
      <MaintenancePlanSection vehicleId={vehicleId} />
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm text-slate-600">
          <span className="font-semibold text-slate-900">{records.length}</span> intervention{records.length > 1 ? "s" : ""}
          {totalCost > 0 && <> · Coût total : <span className="font-semibold text-slate-900">{formatEUR(totalCost)}</span></>}
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700"
          data-testid="add-maintenance-btn"
        >
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showForm ? "Fermer" : "Ajouter une intervention"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="rounded-2xl border border-violet-200 bg-violet-50/30 p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="maintenance-form">
          <div>
            <label className={labelCls}>Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls + " mt-1"} data-testid="maint-type">
              {MAINT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Intitulé *</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Vidange + filtres" className={inputCls + " mt-1"} data-testid="maint-title" />
          </div>
          <div>
            <label className={labelCls}>Date prévue *</label>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className={inputCls + " mt-1"} data-testid="maint-date" />
          </div>
          <div>
            <label className={labelCls}>Statut</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls + " mt-1"} data-testid="maint-status">
              {Object.entries(MAINT_STATUSES).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Coût estimé (€)</label>
            <input type="number" step="0.01" min="0" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} className={inputCls + " mt-1"} data-testid="maint-estimated-cost" />
          </div>
          <div>
            <label className={labelCls}>Coût réel (€)</label>
            <input type="number" step="0.01" min="0" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className={inputCls + " mt-1"} data-testid="maint-cost" />
          </div>
          <div>
            <label className={labelCls}>Fournisseur / garage</label>
            <div className="mt-1">
              <SupplierPicker
                supplierId={form.supplier_id}
                provider={form.provider}
                onChange={({ supplier_id, provider }) => setForm({ ...form, supplier_id, provider })}
                testid="maint-provider"
              />
            </div>
          </div>
          <div>
            <label className={labelCls}>Km au compteur</label>
            <input type="number" min="0" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} className={inputCls + " mt-1"} data-testid="maint-mileage" />
          </div>
          <div>
            <label className={labelCls}>Prochaine échéance (date)</label>
            <input type="date" value={form.next_due_date} onChange={(e) => setForm({ ...form, next_due_date: e.target.value })} className={inputCls + " mt-1"} data-testid="maint-next-date" />
          </div>
          <div>
            <label className={labelCls}>Prochaine échéance (km)</label>
            <input type="number" min="0" value={form.next_due_km} onChange={(e) => setForm({ ...form, next_due_km: e.target.value })} className={inputCls + " mt-1"} data-testid="maint-next-km" />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label className={labelCls}>Notes</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls + " mt-1"} data-testid="maint-notes" />
          </div>
          <div className="sm:col-span-2 lg:col-span-4 flex flex-wrap items-end gap-3 rounded-xl border border-amber-200 bg-amber-50/50 p-3">
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="maint-block-toggle">
              <input
                type="checkbox"
                checked={form.block_vehicle}
                onChange={(e) => setForm({ ...form, block_vehicle: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                data-testid="maint-block-checkbox"
              />
              <span className="text-xs font-semibold text-amber-800">🔧 Bloquer le véhicule pendant l'intervention</span>
            </label>
            {form.block_vehicle && (
              <div>
                <label className={labelCls}>Fin du blocage</label>
                <input
                  type="date"
                  value={form.block_end_date}
                  min={form.date || undefined}
                  onChange={(e) => setForm({ ...form, block_end_date: e.target.value })}
                  className={inputCls + " mt-1"}
                  data-testid="maint-block-end"
                />
              </div>
            )}
            {form.block_vehicle && (
              <p className="text-[10px] text-amber-700 flex-1 min-w-[200px]">Le véhicule sera indisponible à la réservation du {form.date ? formatDateFR(form.date) : "…"} au {formatDateFR(form.block_end_date || form.date) || "…"} (visible dans le Planning et le Calendrier).</p>
            )}
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={saving} className="w-full h-[38px] rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="maint-submit">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Enregistrer
            </button>
          </div>
        </form>
      )}

      {records.length === 0 ? (
        <div className="py-12 text-center rounded-2xl border border-dashed border-slate-200">
          <Wrench className="h-7 w-7 mx-auto text-slate-300 mb-2" />
          <div className="text-sm text-slate-500">Aucune intervention enregistrée.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {records.map((r) => {
            const status = r.status || "done";
            const st = MAINT_STATUSES[status] || MAINT_STATUSES.done;
            const isDone = status === "done";
            return (
            <div key={r.id} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3.5" data-testid={`maintenance-row-${r.id}`}>
              <div className="h-8 w-8 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
                <Wrench className="h-4 w-4 text-violet-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {r.reference && <span className="text-[10px] font-bold text-slate-400">{r.reference}</span>}
                  <span className="text-sm font-semibold text-slate-900">{r.title}</span>
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold">{MAINT_TYPES.find((t) => t.value === r.type)?.label || r.type}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${st.cls}`} data-testid={`maint-status-badge-${r.id}`}>{st.label}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Prévue le {formatDateFR(r.date)}
                  {r.completed_date && <> · Réalisée le {formatDateFR(r.completed_date)}</>}
                  {r.provider && <> · {r.provider}</>}
                  {r.mileage != null && <> · {Number(r.mileage).toLocaleString("fr-FR")} km</>}
                </div>
                {(r.next_due_date || r.next_due_km != null) && (
                  <div className="text-[11px] text-amber-700 mt-1">
                    Prochaine échéance : {r.next_due_date ? formatDateFR(r.next_due_date) : ""}{r.next_due_date && r.next_due_km != null ? " ou " : ""}{r.next_due_km != null ? `${Number(r.next_due_km).toLocaleString("fr-FR")} km` : ""}
                  </div>
                )}
                {r.block_vehicle && ["to_plan", "planned", "in_progress"].includes(status) && (
                  <div className="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold" data-testid={`maint-block-badge-${r.id}`}>
                    🔧 Véhicule bloqué du {formatDateFR(r.date)} au {formatDateFR(r.block_end_date || r.date)}
                    <button
                      onClick={async () => {
                        try {
                          await api.patch(`/vendor/vehicles/${vehicleId}/maintenance/${r.id}`, { block_vehicle: false });
                          toast.success("Véhicule débloqué");
                          load();
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
                      }}
                      className="ml-1 underline decoration-dotted hover:text-amber-950"
                      data-testid={`maint-unblock-${r.id}`}
                    >
                      Débloquer
                    </button>
                  </div>
                )}
                {r.notes && <div className="text-xs text-slate-500 mt-1">{r.notes}</div>}
                {(r.receipts || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {r.receipts.map((rc, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-lg bg-slate-100 text-[10px] font-medium text-slate-600" data-testid={`maint-receipt-${r.id}-${idx}`}>
                        <button onClick={() => openReceipt(rc.path)} className="inline-flex items-center gap-1 hover:text-violet-700">
                          <FileText className="h-3 w-3" /> {rc.name || `Justificatif ${idx + 1}`}
                        </button>
                        {!isDone && (
                          <button onClick={() => deleteReceipt(r, rc.path)} className="p-0.5 rounded hover:bg-red-100 hover:text-red-600" data-testid={`maint-receipt-delete-${r.id}-${idx}`}>
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                {(r.cost != null && r.cost !== 0) ? (
                  <div className="text-sm font-semibold text-slate-900">{formatEUR(r.cost)}</div>
                ) : (r.estimated_cost != null && r.estimated_cost !== 0) ? (
                  <div className="text-sm font-medium text-slate-500">≈ {formatEUR(r.estimated_cost)}</div>
                ) : null}
                <div className="flex items-center justify-end gap-1 mt-1">
                  <label className="h-7 w-7 rounded-md hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center cursor-pointer" title="Ajouter un justificatif" data-testid={`maint-add-receipt-${r.id}`}>
                    <Paperclip className="h-3.5 w-3.5" />
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadReceipt(r, e.target.files[0]); e.target.value = ""; }} />
                  </label>
                  {isDone ? (
                    <button onClick={() => setStatus(r, "in_progress")} className="h-7 px-2 rounded-md hover:bg-slate-100 text-slate-500 text-[10px] font-semibold" data-testid={`maint-reopen-${r.id}`}>
                      Rouvrir
                    </button>
                  ) : (
                    <>
                      <select
                        value={status}
                        onChange={(e) => setStatus(r, e.target.value)}
                        className="text-[11px] rounded-lg border border-slate-200 px-2 py-1 font-semibold outline-none text-slate-700"
                        data-testid={`maint-status-select-${r.id}`}
                      >
                        {Object.entries(MAINT_STATUSES).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
                      </select>
                      <button onClick={() => remove(r.id)} className="h-7 w-7 rounded-md hover:bg-red-50 text-red-500 inline-flex items-center justify-center" title="Supprimer" data-testid={`maintenance-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Onglet Signalements ─────────────────────────────────────────────────────

export const ISSUE_TYPES = [
  { value: "degat", label: "Dégât" },
  { value: "rayure", label: "Rayure" },
  { value: "panne", label: "Panne" },
  { value: "voyant", label: "Voyant moteur" },
  { value: "mecanique", label: "Problème mécanique" },
  { value: "edl", label: "État des lieux" },
  { value: "autre", label: "Autre" },
];
const ISSUE_TYPE_LABELS = Object.fromEntries(ISSUE_TYPES.map((t) => [t.value, t.label]));

const EMPTY_ISSUE = { title: "", severity: "medium", description: "", type: "autre" };

export function IssuesTab({ vehicleId }) {
  const [issues, setIssues] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_ISSUE);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get(`/vendor/vehicles/${vehicleId}/issues`).then((r) => setIssues(r.data.issues || []));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [vehicleId]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/vendor/vehicles/${vehicleId}/issues`, form);
      toast.success("Signalement créé");
      setForm(EMPTY_ISSUE);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (issue, status) => {
    try {
      await api.patch(`/vendor/vehicles/${vehicleId}/issues/${issue.id}`, { status });
      toast.success(status === "resolved" ? "Signalement résolu" : "Statut mis à jour");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!(await confirmAction({
      title: "Supprimer ce signalement ?",
      message: "Le signalement et ses photos seront définitivement supprimés.",
      confirmLabel: "Supprimer le signalement",
      variant: "danger",
    }))) return;
    try {
      await api.delete(`/vendor/vehicles/${vehicleId}/issues/${id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const convert = async (issue) => {
    if (!(await confirmAction({
      title: "Convertir ce signalement en sinistre ?",
      message: `« ${issue.title} » deviendra un dossier sinistre : photos et informations seront reprises automatiquement et le signalement sera clôturé.`,
      confirmLabel: "Confirmer la conversion",
      variant: "warning",
    }))) return;
    try {
      const { data } = await api.post(`/vendor/vehicles/${vehicleId}/issues/${issue.id}/convert-to-claim`, {
        claim_type: issue.type === "rayure" || issue.type === "degat" || issue.type === "edl" ? "carrosserie" : issue.type === "panne" || issue.type === "mecanique" || issue.type === "voyant" ? "panne" : "autre",
      });
      toast.success(`Sinistre ${data.claim.reference} créé`);
      navigate(`/vendor/pilotage?tab=vehicles&claim=${data.claim.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const uploadPhoto = async (issue, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vendor/vehicles/${vehicleId}/issues/${issue.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo ajoutée");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  if (issues === null) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>;

  const openCount = issues.filter((i) => i.status !== "resolved").length;

  return (
    <div className="space-y-4" data-testid="issues-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm text-slate-600">
          <span className="font-semibold text-slate-900">{openCount}</span> signalement{openCount > 1 ? "s" : ""} en cours · {issues.length} au total
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700"
          data-testid="add-issue-btn"
        >
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showForm ? "Fermer" : "Nouveau signalement"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="rounded-2xl border border-violet-200 bg-violet-50/30 p-4 grid sm:grid-cols-3 gap-3" data-testid="issue-form">
          <div className="sm:col-span-3">
            <label className={labelCls}>Titre *</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Rayure portière avant droite" className={inputCls + " mt-1"} data-testid="issue-title" />
          </div>
          <div className="sm:col-span-3 flex justify-end">
            <button type="submit" disabled={saving} className="h-[38px] px-6 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="issue-submit">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Créer
            </button>
          </div>
        </form>
      )}

      {issues.length === 0 ? (
        <div className="py-12 text-center rounded-2xl border border-dashed border-slate-200">
          <CheckCircle2 className="h-7 w-7 mx-auto text-emerald-300 mb-2" />
          <div className="text-sm text-slate-500">Aucun signalement. Tout va bien !</div>
        </div>
      ) : (
        <div className="space-y-2">
          {issues.map((i) => {
            const sev = SEVERITIES[i.severity] || SEVERITIES.medium;
            const st = ISSUE_STATUSES[i.status] || ISSUE_STATUSES.open;
            const converted = !!i.converted_claim_id;
            return (
              <div key={i.id} className={`rounded-xl border bg-white p-3.5 ${i.status === "resolved" ? "border-slate-100 opacity-70" : "border-slate-200"}`} data-testid={`issue-row-${i.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                    <AlertTriangle className={`h-4 w-4 ${i.severity === "high" ? "text-red-600" : i.severity === "medium" ? "text-amber-500" : "text-slate-400"}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {i.reference && <span className="text-[10px] font-bold text-slate-400">{i.reference}</span>}
                      <span className={`text-sm font-semibold text-slate-900 ${i.status === "resolved" ? "line-through" : ""}`}>{i.title}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${sev.cls}`}>{sev.label}</span>
                      {i.type && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600">{ISSUE_TYPE_LABELS[i.type] || i.type}</span>}
                      {converted && (
                        <button
                          onClick={() => navigate(`/vendor/pilotage?tab=vehicles&claim=${i.converted_claim_id}`)}
                          className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-100 text-violet-700 hover:bg-violet-200"
                          data-testid={`issue-converted-badge-${i.id}`}
                        >
                          Converti en {i.claim_reference}
                        </button>
                      )}
                    </div>
                    {i.description && <div className="text-xs text-slate-500 mt-0.5">{i.description}</div>}
                    <div className="text-[10px] text-slate-400 mt-1">
                      Créé le {formatDateFR(i.created_at?.split("T")[0])}
                      {i.resolved_at && <> · Résolu le {formatDateFR(i.resolved_at.split("T")[0])}</>}
                      {i.inspection_id && <> · Issu d'un état des lieux</>}
                    </div>
                    {(i.photos || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {i.photos.map((p, idx) => (
                          <AuthImg key={idx} path={p} className="h-12 w-12 rounded-lg object-cover border border-slate-200" data-testid={`issue-photo-${i.id}-${idx}`} />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {!converted && (
                      <>
                        <label className="h-7 w-7 rounded-md hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center cursor-pointer" title="Ajouter une photo" data-testid={`issue-add-photo-${i.id}`}>
                          <Camera className="h-3.5 w-3.5" />
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadPhoto(i, e.target.files[0]); e.target.value = ""; }} />
                        </label>
                        <button onClick={() => convert(i)} className="h-7 px-2 rounded-md hover:bg-violet-50 text-violet-600 inline-flex items-center justify-center gap-1 text-[10px] font-semibold" title="Convertir en sinistre" data-testid={`issue-convert-${i.id}`}>
                          <ShieldAlert className="h-3.5 w-3.5" /> Sinistre
                        </button>
                        <select
                          value={i.status}
                          onChange={(e) => setStatus(i, e.target.value)}
                          className={`text-[11px] rounded-lg border px-2 py-1 font-semibold outline-none ${st.cls}`}
                          data-testid={`issue-status-${i.id}`}
                        >
                          <option value="open">Ouvert</option>
                          <option value="in_progress">En cours</option>
                          <option value="resolved">Résolu</option>
                        </select>
                        <button onClick={() => remove(i.id)} className="h-7 w-7 rounded-md hover:bg-red-50 text-red-500 inline-flex items-center justify-center" title="Supprimer" data-testid={`issue-delete-${i.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                    {converted && <span className={`text-[11px] rounded-lg border px-2 py-1 font-semibold ${st.cls}`}>{st.label}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
